import {
  encodeResearchAgentResourceId,
  RESEARCH_AGENT_ID,
} from "@repo/app-types/entities/researchAgent";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import * as Sentry from "@sentry/react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { v4 as uuid } from "uuid";
import {
  invalidateMastraThreadAction,
  useCreateMastraThreadMutation,
  useLazyMastraThreadQuery,
} from "../../api/entities/mastraThread.js";
import { useHideAlpharunBranding } from "../../helpers/fountain.js";
import { useMainNavigationFeatureFlags } from "../../helpers/mainNavigation.js";
import {
  readStoredBooleanPreference,
  writeStoredBooleanPreference,
} from "../../helpers/storedPreference.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { MastraChatProvider } from "../agents/mastra/mastraChatContext.js";
import { useMastraChat } from "../agents/mastra/useMastraChat.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import {
  RESEARCH_THREAD_ID_PARAM,
  ResearchAgentContext,
  researchAgentThreadMetadataSchema,
  type ResearchAgentProps,
  type ResearchAgentThreadMetadata,
} from "./researchAgentContext.js";
import {
  useCopilotPageContext,
  useCopilotPanelRoutePathname,
} from "./researchAgentPageContext.js";
import { useIsDockedPanelViewport } from "./researchAgentViewport.js";

export interface ResearchAgentProviderProps {
  children?: ReactNode;
}

interface ThreadState {
  localThreadId: string;
  initialUserMessageText?: string;
  remote?: RemoteThreadState;
  isNotFound?: boolean;
}

interface RemoteThreadState {
  id: string;
  resourceId: string;
  /**
   * Known at selection time; empty until Mastra generates one. Required so a
   * new source of threads can't quietly leave it out and fall back to the
   * placeholder title.
   */
  title: string | undefined;
  metadata: ResearchAgentThreadMetadata;
}

function initializeThreadState(): ThreadState {
  return {
    localThreadId: uuid(),
  };
}

/**
 * Threads are company-scoped; new ones carry no scope metadata. Grounding
 * comes from `pageContext` instead, sent with every message.
 */
const NEW_THREAD_METADATA: ResearchAgentThreadMetadata = {};

/** localStorage key remembering whether the side panel was left open. */
const panelExpandedStorageKey = "alpharun.researchAgentExpanded";

// Mastra titles a thread from its first user message, but it does so in the
// background: the title lands a second or two *after* the response stream
// closes, so refreshing the thread the moment the turn ends still reads the
// empty title it was created with. Poll for it instead.
const THREAD_TITLE_POLL_INTERVAL = 1000; // ms
const THREAD_TITLE_POLL_ATTEMPTS = 10;

export const ResearchAgentProvider: FC<ResearchAgentProviderProps> = (
  props,
) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(() =>
    readStoredBooleanPreference(panelExpandedStorageKey),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEmbeddedChatOpen, setIsEmbeddedChatOpen] = useState(false);
  const [composerDraft, setComposerDraft] = useState("");
  const [composerFocusRequestId, setComposerFocusRequestId] = useState(0);
  const isDockedPanelViewport = useIsDockedPanelViewport();

  useEffect(() => {
    writeStoredBooleanPreference(panelExpandedStorageKey, isExpanded);
  }, [isExpanded]);

  // Configuration routes put the copilot on screen by default. Closing it
  // there sticks until the user next navigates to a configuration page.
  const copilotPanelRoutePathname = useCopilotPanelRoutePathname();
  useEffect(() => {
    if (copilotPanelRoutePathname !== null) {
      setIsExpanded(true);
    }
  }, [copilotPanelRoutePathname]);

  // A thread we could not load isn't worth restoring when the chat reopens.
  const forgetNotFoundThread = useCallback(() => {
    setThreadState((prevThread) =>
      prevThread.isNotFound ? { localThreadId: uuid() } : prevThread,
    );
  }, []);

  // Which surface hosts the chat depends on the viewport: the docked panel
  // can't fit on narrow ones, where the expanded modal stands in for it.
  // Callers open and close the chat through these rather than picking.
  const onOpenChat = useCallback(() => {
    if (isDockedPanelViewport) {
      setIsExpanded(true);
    } else {
      setIsModalOpen(true);
    }
  }, [isDockedPanelViewport]);

  const onCloseChat = useCallback(() => {
    setIsExpanded(false);
    setIsModalOpen(false);
    setIsEmbeddedChatOpen(false);
    forgetNotFoundThread();
  }, [forgetNotFoundThread]);

  const onModalOpenChange = useCallback(
    (newIsModalOpen: boolean) => {
      setIsModalOpen(newIsModalOpen);
      if (!newIsModalOpen) {
        forgetNotFoundThread();
      }
    },
    [forgetNotFoundThread],
  );

  // Availability is decided here rather than at the button, so that the
  // surfaces that open the chat without it — a `?research_thread_id=` link and
  // a panel restored from the last visit — cannot reach an agent this company
  // doesn't get.
  const isEnabledForCompany = useIsResearchAgentEnabledForCompany();
  const teammateResourceId = useResearchAgentResourceId();
  const resourceId = isEnabledForCompany ? teammateResourceId : undefined;

  const [threadState, setThreadState] = useState<ThreadState>(
    initializeThreadState,
  );

  // What the user is currently viewing, sent with each message so the agent can
  // ground its answer in the on-screen resource. Read at send time, so it
  // reflects the route the user is on when they hit send — not when the thread
  // was opened.
  const pageContext = useCopilotPageContext();

  // Work the page needs finished before a message goes out, registered by the
  // editor that owns it. Held in a ref so registering re-renders nothing.
  const beforeSendRef = useRef<Set<() => Promise<unknown>>>(new Set());
  const registerBeforeSend = useCallback(
    (beforeSend: () => Promise<unknown>) => {
      beforeSendRef.current.add(beforeSend);
      return () => {
        beforeSendRef.current.delete(beforeSend);
      };
    },
    [],
  );

  const currentRemoteThreadId = threadState.remote?.id;
  const onThreadSelect = useCallback(
    (remoteThread: {
      id: string;
      resourceId: string;
      title: string | undefined;
      metadata: ResearchAgentThreadMetadata;
    }) => {
      // Re-selecting the current thread (e.g. backing out of the panel's
      // history view) is a no-op, keeping the half-typed draft. The caller's
      // thread copy comes from the history list cache, which can lag the
      // thread's own state.
      if (remoteThread.id === currentRemoteThreadId) {
        return;
      }
      setComposerDraft("");
      setThreadState({ localThreadId: uuid(), remote: remoteThread });
    },
    [currentRemoteThreadId],
  );

  const onNewThreadSelect = useCallback(
    (options?: { initialUserMessageText?: string }) => {
      // For unsaved threads, return to this thread when starting a new thread
      if (currentRemoteThreadId !== undefined) {
        setComposerDraft("");
      }
      setComposerFocusRequestId((previousId) => previousId + 1);
      setThreadState({
        localThreadId: uuid(),
        initialUserMessageText: options?.initialUserMessageText,
      });
    },
    [currentRemoteThreadId],
  );

  const [fetchThread] = useLazyMastraThreadQuery();

  // Sync the currently visible thread to a URL query string parameter
  const [searchParams, setSearchParams] = useSearchParams();
  const currentThreadIdParam = searchParams.get(RESEARCH_THREAD_ID_PARAM);
  const didHandleThreadIdParam = useRef(false);

  // If there's a thread ID in the URL upon mounting, load that thread. A link
  // always opens the modal, whatever the viewport: someone following a shared
  // chat came for the conversation, not for the page behind it.
  useEffect(() => {
    if (didHandleThreadIdParam.current) {
      return;
    }

    // Wait for the resource id rather than settling the ref without it: it is
    // undefined until the teammate loads, and stays undefined for a company
    // that doesn't get the agent at all.
    if (!resourceId) {
      return;
    }

    if (!currentThreadIdParam) {
      didHandleThreadIdParam.current = true;
      return;
    }

    setIsModalOpen(true);

    if (currentThreadIdParam === "new") {
      didHandleThreadIdParam.current = true;
      return;
    }

    fetchThread({ agentId: RESEARCH_AGENT_ID, threadId: currentThreadIdParam })
      .unwrap()
      .then((thread) => {
        setThreadState({
          localThreadId: uuid(),
          remote: {
            id: thread.id,
            resourceId: thread.resourceId,
            title: thread.title,
            metadata: researchAgentThreadMetadataSchema.parse(thread.metadata),
          },
        });
      })
      .catch(() => {
        setThreadState({
          localThreadId: uuid(),
          isNotFound: true,
        });
      })
      .finally(() => {
        didHandleThreadIdParam.current = true;
      });
  }, [currentThreadIdParam, fetchThread, resourceId]);

  const onThreadIdParamChange = useEffectEvent(
    (newThreadIdParam: string | null) => {
      // Don't call `setSearchParams` at all unless we have to, because it wipes out stuff like the PostHog dev toolbar auth URL hash.
      if (currentThreadIdParam === newThreadIdParam) {
        return;
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (newThreadIdParam === null) {
            next.delete(RESEARCH_THREAD_ID_PARAM);
          } else {
            next.set(RESEARCH_THREAD_ID_PARAM, newThreadIdParam);
          }
          return next;
        },
        { replace: true },
      );
    },
  );

  const threadId = threadState.remote?.id;
  // The URL mirrors the modal and nothing else. The docked panel is a piece of
  // app furniture the user leaves open across navigations, so writing to the
  // URL from it would rewrite the address bar as a side effect of scrolling
  // through chats, and hand out links carrying a thread the sender never meant
  // to share. The modal is the deliberate, shareable surface.
  const isResearchAgentAvailable = resourceId !== undefined;
  const isPanelVisible = isExpanded && isDockedPanelViewport;
  const isChatVisible = isPanelVisible || isModalOpen || isEmbeddedChatOpen;
  const threadIdParam =
    isResearchAgentAvailable && isModalOpen ? (threadId ?? "new") : null;
  useEffect(() => {
    onThreadIdParamChange(threadIdParam);
  }, [threadIdParam, onThreadIdParamChange]);

  const contextValue: ResearchAgentProps | undefined = useMemo(() => {
    // Show the research agent only when we were able to calculate a Mastra
    // resourceId, which indicates the user has access.
    if (!resourceId) {
      return undefined;
    }

    return {
      isPanelVisible,
      isChatVisible,
      onOpenChat,
      onCloseChat,
      isModalOpen,
      onModalOpenChange,
      isEmbeddedChatOpen,
      onEmbeddedChatOpenChange: setIsEmbeddedChatOpen,
      resourceId,
      localThreadId: threadState.localThreadId,
      threadId: threadState.remote?.id,
      threadTitle: threadState.remote?.title,
      threadResourceId: threadState.remote?.resourceId ?? resourceId,
      threadMetadata: threadState.remote?.metadata ?? NEW_THREAD_METADATA,
      isThreadNotFound: threadState.isNotFound ?? false,
      onThreadSelect,
      onNewThreadSelect,
      composerFocusRequestId,
      composerDraft,
      onComposerDraftChange: setComposerDraft,
      registerBeforeSend,
    };
  }, [
    isPanelVisible,
    isChatVisible,
    onOpenChat,
    onCloseChat,
    isModalOpen,
    onModalOpenChange,
    isEmbeddedChatOpen,
    resourceId,
    threadState,
    onThreadSelect,
    onNewThreadSelect,
    composerFocusRequestId,
    composerDraft,
    registerBeforeSend,
  ]);

  const dispatch = useAppDispatch();
  const [createThread] = useCreateMastraThreadMutation();
  const createResearchAgentThread = useCallback(
    async (
      resourceId: string,
      metadata: ResearchAgentThreadMetadata,
    ): Promise<RemoteThreadState> => {
      const newThread = await createThread({
        agentId: RESEARCH_AGENT_ID,
        resourceId,
        metadata,
      }).unwrap();

      return {
        id: newThread.id,
        resourceId: newThread.resourceId,
        title: newThread.title,
        metadata: researchAgentThreadMetadataSchema.parse(newThread.metadata),
      };
    },
    [createThread],
  );

  // Polling outlives the turn that started it, so it has to stop when the
  // provider goes away: a lazy query triggered after unmount registers a
  // subscription that nothing is left to release.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Refetches the thread until Mastra's generated title shows up, so the chat
   * header and the history list pick it up without a page reload.
   */
  const refreshGeneratedThreadTitle = useCallback(
    async (threadId: string) => {
      for (let attempt = 0; attempt < THREAD_TITLE_POLL_ATTEMPTS; attempt++) {
        if (!isMountedRef.current) {
          return;
        }

        // `false` skips the cached value, so every attempt hits the server and
        // writes what it finds back into the thread's cache entry.
        const { data: thread, isError } = await fetchThread(
          { agentId: RESEARCH_AGENT_ID, threadId },
          false,
        );
        if (thread?.title || isError) {
          break;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, THREAD_TITLE_POLL_INTERVAL),
        );
      }

      if (!isMountedRef.current) {
        return;
      }

      // The history list carries the title too, and has its own cache entry.
      dispatch(
        invalidateMastraThreadAction({ agentId: RESEARCH_AGENT_ID, threadId }),
      );
    },
    [dispatch, fetchThread],
  );

  // Determine if the current thread is readonly (not owned by the authenticated user)
  const isOwnThread =
    !threadState.remote || threadState.remote.resourceId === resourceId;

  const chat = useMastraChat({
    agentId: RESEARCH_AGENT_ID,
    localThreadId: threadState.localThreadId,
    // Only load history if we have a selected remote thread
    loadThreadId: threadState.remote?.id,
    onSend: async () => {
      if (!resourceId) {
        throw new Error("Cannot send message without a valid resource ID");
      }

      // Prevent sending messages in readonly mode
      if (!isOwnThread) {
        throw new Error("Cannot send messages to a shared thread");
      }

      // A page's unsaved edits are what the user is asking about. A failed
      // flush reports itself in that page's own editor, so the send proceeds
      // against whatever is stored.
      await Promise.all(
        [...beforeSendRef.current].map((beforeSend) =>
          beforeSend().catch(() => undefined),
        ),
      );

      const localThreadId = threadState.localThreadId;
      let remoteThread = threadState.remote;
      let onFinish: (() => void) | undefined = undefined;

      // If we're in a new thread state, create the thread first
      if (!remoteThread) {
        // Create the new thread in Mastra
        const newRemoteThread = await createResearchAgentThread(
          resourceId,
          NEW_THREAD_METADATA,
        );

        // Update our state to reflect our local thread now has a remote counterpart
        setThreadState((prevState) => {
          if (prevState.localThreadId === localThreadId) {
            return { ...prevState, remote: newRemoteThread };
          }

          return prevState;
        });

        // Send the message in the newly created thread
        remoteThread = newRemoteThread;
        onFinish = () => {
          // After the first turn, pick up the server-side generated title.
          // A failed refresh only costs the title until the next reload, so
          // report it rather than surfacing it to the user.
          refreshGeneratedThreadTitle(newRemoteThread.id).catch(
            (error: unknown) => Sentry.captureException(error),
          );
        };
      }

      return {
        thread: {
          resourceId,
          threadId: remoteThread.id,
        },
        requestContext: {
          ...remoteThread.metadata,
          ...(pageContext ? { pageContext } : {}),
        },
        onFinish,
      };
    },
  });

  const prevLocalThreadId = useRef<string | undefined>(undefined);
  const onThreadChange = useEffectEvent((localThreadId: string) => {
    // Make sure we do this only once per thread change
    if (prevLocalThreadId.current === localThreadId) {
      return;
    }
    prevLocalThreadId.current = localThreadId;

    // Send initial message if provided
    if (threadState.initialUserMessageText) {
      chat.sendMessage(threadState.initialUserMessageText);
    }
  });

  // Handle thread changes
  const localThreadId = threadState.localThreadId;
  useEffect(() => {
    onThreadChange(localThreadId);
  }, [localThreadId, onThreadChange]);

  return (
    <ResearchAgentContext.Provider value={contextValue}>
      <MastraChatProvider chat={chat}>{props.children}</MastraChatProvider>
    </ResearchAgentContext.Provider>
  );
};

/**
 * Whether the company gets the research agent at all. It answers over the
 * non-recruiting workspaces, so it needs at least one of them, and it carries
 * Alpharun branding, so it follows the same whitelabeling rule as the logo.
 */
function useIsResearchAgentEnabledForCompany() {
  const { hasTeamPerformanceTab, hasTraining, hasAgentsTab } =
    useMainNavigationFeatureFlags();
  const hideAlpharunBranding = useHideAlpharunBranding();

  return (
    (hasTeamPerformanceTab || hasTraining || hasAgentsTab) &&
    !hideAlpharunBranding
  );
}

/**
 * Determines the Mastra `resourceId` for the current teammate.
 *
 * What a teammate's role lets the agent read is decided server-side, by the
 * `teammateScope` the agent's dependencies derive from that role — a team
 * member's tools are bound to their own work. So the route the user is on
 * does not decide whether they get the agent.
 */
function useResearchAgentResourceId() {
  const authenticatedTeammate = useAppSelector(selectTeammate);

  // If we don't have a teammate associated with a company, we cannot show the research agent.
  if (!authenticatedTeammate?.company_id) {
    return undefined;
  }

  return encodeResearchAgentResourceId(
    authenticatedTeammate.company_id,
    authenticatedTeammate.id,
  );
}
